import React from 'react';
import { 
  oralNasalOptions, 
  cuffedOptions, 
  ivAccessOptions, 
  preOxygenationOptions, 
  rapidSequenceOptions, 
  throatPackOptions 
} from '../constants/options';

const IntubationMonitoringSection = ({ formData, handleChange, handleMonitoringChange, setFormData }) => {
  return (
    <div className="space-y-4">
      <div className="border-2 border-teal-200 rounded-xl p-4 bg-gradient-to-br from-teal-50 to-white shadow-sm">
        <h3 className="text-lg font-bold mb-4 text-teal-800 flex items-center">
          <div className="w-2 h-6 bg-teal-600 rounded-full mr-3"></div>
          Intubation & Induction Details
        </h3>
        <div className="grid grid-cols-2 gap-3">
          {[
            { key: "oralNasal", label: "Route", placeholder: "Oral/Nasal", type: "select", options: oralNasalOptions },
            { key: "cuffed", label: "Cuff Type", placeholder: "Cuffed/Uncuffed", type: "select", options: cuffedOptions },
            { key: "size", label: "Tube Size", placeholder: "e.g., 7.5mm" },
            { key: "scopeGrade", label: "Laryngoscope", placeholder: "Grade I-IV" },
            { key: "ivAccess", label: "I.V Access", type: "select", options: ivAccessOptions },
            { key: "preOxygenation", label: "Pre-Oxygenation", type: "select", options: preOxygenationOptions },
            { key: "rapidSequence", label: "Rapid Sequence Induction", type: "select", options: rapidSequenceOptions },
            { key: "inhalationalAgent", label: "Inhalational Agent", placeholder: "e.g. Sevoflurane" },
            { key: "throatPack", label: "Throat Pack Used", type: "select", options: throatPackOptions },
          ].map(({ key, label, placeholder, type, options }) => (
            <div key={key}>
              <label className="block text-xs font-semibold mb-1 text-gray-700">
                {label}
                {["oralNasal", "cuffed", "size", "scopeGrade", "inhalationalAgent"].includes(key) && <span className="text-red-500 ml-1">*</span>}
              </label>
              {type === "select" ? (
                <select
                  value={formData[key] || ""}
                  onChange={handleChange(key)}
                  className="border-2 border-gray-300 px-2 py-1 w-full rounded-lg text-xs focus:border-teal-500 focus:outline-none transition-colors"
                  aria-label={label}
                >
                  <option value="" disabled>
                    Select
                  </option>
                  {options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={formData[key] || ''}
                  onChange={handleChange(key)}
                  placeholder={placeholder}
                  className="border-2 border-gray-300 px-2 py-1 w-full rounded-lg text-xs focus:border-teal-500 focus:outline-none transition-colors"
                  aria-label={label}
                />
              )}
            </div>
          ))}
          <div className="col-span-2">
            <label className="block text-xs font-semibold mb-1 text-gray-700">Tube Position</label>
            <input
              type="text"
              value={formData.inOut || ''}
              onChange={handleChange("inOut")}
              placeholder="Depth at teeth/lips"
              className="border-2 border-gray-300 px-2 py-1 w-full rounded-lg text-xs focus:border-teal-500 focus:outline-none transition-colors"
              aria-label="Tube Position"
            />
          </div>
        </div>
      </div>

      <div className="border-2 border-teal-200 rounded-xl p-4 bg-gradient-to-br from-teal-50 to-white shadow-sm">
        <h3 className="text-lg font-bold mb-4 text-teal-800 flex items-center">
          <div className="w-2 h-6 bg-teal-600 rounded-full mr-3"></div>
          Monitoring Setup
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm mb-4">
          {Object.entries({
            spo2: "SpO₂",
            ecg: "ECG",
            nibp: "NIBP",
            etco2: "ETCO₂",
            fio2: "FiO₂",
            temp: "Temperature",
            cvp: "CVP",
            arterialLine: "Arterial Line",
            uop: "Urine Output",
          }).map(([key, label]) => (
            <label
              key={key}
              className="flex items-center p-2 rounded-lg hover:bg-teal-100 transition-colors cursor-pointer"
            >
              <input
                type="checkbox"
                checked={formData.monitoring?.[key] || false}
                onChange={handleMonitoringChange(key)}
                className="mr-2 w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                aria-label={label}
              />
              <span className="font-medium">{label}</span>
            </label>
          ))}
        </div>
        <div className="pt-3 border-t border-teal-200 space-y-2">
          <label className="flex items-center text-sm font-medium">
            <input
              type="checkbox"
              checked={formData.equipmentChecked || false}
              onChange={(e) => setFormData({ ...formData, equipmentChecked: e.target.checked })}
              className="mr-3 w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
              aria-label="Equipment Safety Check"
            />
            Equipment Safety Check Complete
          </label>
          <label className="flex items-center text-sm font-medium">
            <input
              type="checkbox"
              checked={formData.consentChecked || false}
              onChange={(e) => setFormData({ ...formData, consentChecked: e.target.checked })}
              className="mr-3 w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
              aria-label="Patient Consent Verified"
            />
            Patient Consent Verified
          </label>
        </div>
      </div>
    </div>
  );
};

export default IntubationMonitoringSection;
